import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { Archive, Image } from '@common/core/models';
import {
  UiRoleManagerService,
  ImagesService,
  ArchivesService,
  LoaderService,
  StoreService,
  FilesService,
  ToastMessagesService,
} from '@services';
import { EBitfUiMessageType, ERoleActions } from '@common/enums';
import { IBitfApiResponse, IBitfGraphQlResponse } from '@common/interfaces';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { BitfFile } from '@common/libs/bitforce/core/models';
import { bitfPromptToDownloadCorsImage } from '@common/libs/bitforce/utils/bitf-files.utils';

@Component({
  selector: 'mpa-archive-image-manager',
  templateUrl: './archive-image-manager.component.html',
  styleUrls: ['./archive-image-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ArchiveImageManagerComponent implements OnInit {
  @ViewChild(FileUploaderComponent) fileUploader: FileUploaderComponent;
  get selectedItem(): Archive {
    return this.storeService.store.selectedItem;
  }

  eRoleActions = ERoleActions;

  get imagesList(): Image[] {
    return this.selectedItem?.images || [];
  }

  constructor(
    public uiRoleManagerService: UiRoleManagerService,
    private loaderService: LoaderService,
    private translateService: TranslateService,
    private imagesService: ImagesService,
    private archiveService: ArchivesService,
    private filesService: FilesService,
    private toastMessagesService: ToastMessagesService,
    public storeService: StoreService
  ) {}

  ngOnInit(): void {}

  trackByImageId(index: number, image: Image): number {
    return image.id;
  }

  onDrop(event: CdkDragDrop<Image[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.imagesList, event.previousIndex, event.currentIndex);
      this.updateImagesList(this.imagesList);
    }
  }

  onChangeImageVisibilityOnPrint(image: Image) {
    const body = new Image(
      {},
      { caption: { ...image.caption, hiddenOnPrint: !image.caption.hiddenOnPrint } }
    );
    this.imagesService
      .update(
        {
          id: image.id,
          body,
        },
        image
      )
      .subscribe(() => {
        this.toastMessagesService.show({
          title: image.caption.hiddenOnPrint
            ? this.translateService.instant('ITEM.DETAIL.IMAGE_HIDDEN_ON_PRINT')
            : this.translateService.instant('ITEM.DETAIL.IMAGE_VISIBLE_ON_PRINT'),
          type: EBitfUiMessageType.SUCCESS,
        });
      });
  }

  onDownloadImage(image: Image) {
    bitfPromptToDownloadCorsImage({ fileUrl: image.getImageByFormat().url, fileName: image.name });
  }

  onRemoveImage(image: Image) {
    if (!window.confirm(this.translateService.instant('ITEM.DETAIL.CONFIRM_REMOVING_IMAGE'))) {
      return;
    }
    this.loaderService.show();
    this.imagesService
      .delete({ id: image.id })
      .pipe(switchMap(_ => this.archiveService.getItemById({ id: this.storeService.store.selectedItem.id })))
      .subscribe((response: IBitfApiResponse<Archive>) => {
        this.storeService.updateSelectedItem({ images: response.content.images });
      });
  }

  onUpload() {
    const images: BitfFile[] = Array.from(this.fileUploader.uploadableFiles);

    const observableList = images.map(image =>
      this.filesService.createImage({
        variables: { file: image },
        context: { useMultipart: true },
        disableHideLoader: true,
      })
    );

    if (observableList.length > 0) {
      this.loaderService.show();
      this.filesService
        .getThrottledObservables(observableList, 1)
        .pipe(
          switchMap((responses: IBitfGraphQlResponse<Image>[]) => {
            const updateArchive = new Archive(undefined, {
              id: this.selectedItem.id,
              images: this.selectedItem.images,
            });
            updateArchive.images.push(...responses.map(response => response.content));
            return this.archiveService.updateItem({
              disableHideLoader: true,
              body: updateArchive,
            });
          }),
          tap((response: IBitfGraphQlResponse<Archive>) => {
            this.storeService.updateSelectedItem(response.content);
          }),
          finalize(() => {
            this.loaderService.hide();
            this.fileUploader.removeUploadedFiles();
          })
        )
        .subscribe();
    }
  }

  private updateImagesList(imagesList: Image[]): void {
    this.loaderService.show();
    this.archiveService
      .updateItem({
        disableHideLoader: true,
        body: new Archive(undefined, {
          id: this.selectedItem.id,
          images: imagesList,
        }),
      })
      .pipe(
        tap((response: IBitfGraphQlResponse<Archive>) => {
          this.storeService.updateSelectedItem({ images: response.content.images });
        }),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe();
  }
}
